import React from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useGlobalContext } from '../../../context/GlobalContext';

const HeroSection = () => {
  const { globalSettings: settings, resumeUrl, loading } = useGlobalContext();

  const defaultStats = [
    { label: "Projects Completed", value: "5+", icon: "Rocket" },
    { label: "National Research Internships", value: "2+", icon: "Target" },
    { label: "NASA & ISRO", value: "Collaboration", icon: "Globe" }
  ];

  const stats = settings?.hero_stats || defaultStats;
  const isAvailable = settings?.availability_status === 'available';

  return (
    <section className="relative bg-background pt-20 lg:pt-24 pb-16 lg:pb-20 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-32 h-32 bg-accent rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-primary rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="order-2 lg:order-1">
            <div className="space-y-6">
              <div className="space-y-4">
                <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-text-primary leading-tight">
                  Passion for Space.
                </h1>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-text-primary leading-tight"><span className="block text-accent">Precision in Data.</span></h2>
                <p className="text-lg lg:text-lg text-text-secondary leading-relaxed">
                  {settings?.hero_description || "I’m a Computer Science Engineer, Software Developer, and Space Technology Researcher specializing in Artificial Intelligence, Remote Sensing, Earth Observation, and Geospatial Analytics. I work on turning satellite and UAV data into real-world insights."}
                </p>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-8 py-4">
                {loading ? (
                  <div className="h-12 w-full animate-pulse bg-white/5 rounded-lg max-w-md"></div>
                ) : (
                  stats.map((stat, idx) => (
                    <div key={idx} className="text-center group">
                      <div className="font-heading font-bold text-2xl lg:text-3xl text-text-primary group-hover:text-accent transition-smooth">{stat.value}</div>
                      <div className="text-sm text-text-secondary pr-2 break-words">{stat.label}</div>
                    </div>
                  ))
                )}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button 
                  variant="primary" 
                  size="lg"
                  iconName="ArrowRight" 
                  iconPosition="right"
                  onClick={() => {}}
                >
                  <Link 
                    to="/projects" 
                    className="flex items-center" 
                    title="View Sudhan R's Full Portfolio & Featured Projects"
                    aria-label="Navigate to Projects Page"
                  >
                    View My Work
                  </Link>
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  iconName="Download"
                  className="bg-[#3498DB]" 
                  iconPosition="left"
                  onClick={() => {}}
                >
                  <a
                    href={resumeUrl || "/assets/Resume/Resume PDF.pdf"}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Download Sudhan R's Master Resume (PDF)"
                    aria-label="Download Resume Document"
                    className="flex items-center"
                  >
                    Download Resume
                  </a>
                </Button>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-4 pt-6">
                <span className="text-sm text-text-secondary">Connect with me:</span>
                <div className="flex items-center gap-3">
                  <a 
                    href={settings?.social_linkedin || "https://www.linkedin.com/in/sudhan23082004/"} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="Sudhan R on LinkedIn"
                    aria-label="Visit LinkedIn Profile"
                    className="p-2 rounded-lg bg-surface hover:bg-accent text-text-secondary hover:text-white transition-smooth border border-white/5"
                  >
                    <Icon name="Linkedin" size={20} />
                  </a>
                  <a 
                    href={settings?.social_github || "https://github.com/Sudhan-23082004"} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="Sudhan R on GitHub"
                    aria-label="Visit GitHub Repository Profile"
                    className="p-2 rounded-lg bg-surface hover:bg-accent text-text-secondary hover:text-white transition-smooth border border-white/5"
                  >
                    <Icon name="Github" size={20} />
                  </a>
                  <a 
                    href={settings?.social_orcid || "https://orcid.org/0009-0007-0461-2150"} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title="Sudhan R ORCID Scientific Publications"
                    aria-label="Visit ORCID Author Profile"
                    className="p-2 rounded-lg bg-surface hover:bg-accent text-text-secondary hover:text-white transition-smooth border border-white/5"
                  >
                    <Icon name="Link" size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 flex justify-center lg:justify-center">
            <div className="relative">
              <div className="w-80 h-80 lg:w-96 lg:h-96 rounded-2xl overflow-hidden shadow-primary relative bg-white/5">
                {loading ? (
                  <div className="absolute inset-0 bg-white/5 animate-pulse rounded-2xl" />
                ) : (
                  <Image
                    src={settings?.about_hero_pic || "/assets/images/Sudhan_2026_v3.png"}
                    alt="Professional headshot of portfolio owner"
                    className="w-full h-full object-cover animate-fade-in"
                    width={400}
                    height={400}
                  />
                )}
              </div>
              {/* Floating Badge */}
              <div className="absolute -bottom-4 -right-6 bg-surface/80 backdrop-blur-md rounded-xl p-4 border border-white/10 shadow-xl">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full animate-pulse shadow-lg ${isAvailable ? 'bg-success shadow-success/50' : 'bg-warning shadow-warning/50'}`}></div>
                  <span className="text-sm font-medium text-text-primary">
                    {loading ? 'Consulting Status...' : settings?.availability_status_text || (isAvailable ? 'Available for work' : 'Busy with Mission')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;